import { DefaultLocale } from '../providers/tolgee';

export type TolgeeSSRInput = {
  language?: string;
  staticData?: Record<string, unknown>;
};

/**
 * Builds the `ssr` prop for `<TolgeeProvider>`.
 *
 * Always returns an object so Tolgee can hydrate correctly on the client.
 *
 * When the requested language is a non-default locale with no matching
 * `staticData` key, the `language` field is omitted from the returned config.
 * Tolgee then falls back to `defaultLanguage` (en-US) for the SSR render,
 * which we always have bundled. This avoids two broken alternatives:
 *
 * - Passing `language: 'en-GB'` with no en-GB data → Tolgee SSRs with empty
 *   strings, BackendFetch loads en-GB on the client → hydration mismatch →
 *   layout shift that scrolls the page.
 * - Passing `ssr={undefined}` → Tolgee skips SSR entirely, server renders with
 *   no translations, client loads them → same layout shift problem.
 */
export function buildTolgeeSSRProp(
  tolgeeSSR: TolgeeSSRInput | undefined,
  defaultLocaleData: Record<string, unknown>,
): TolgeeSSRInput {
  const language = tolgeeSSR?.language;
  const incomingStaticData = tolgeeSSR?.staticData;

  const staticData = {
    ...(incomingStaticData || {}),
    [DefaultLocale]: defaultLocaleData,
  };

  // Non-default locale with no matching static data: omit `language` so
  // Tolgee SSRs with the default locale instead of an unsupported one.
  if (language && language !== DefaultLocale && !incomingStaticData?.[language]) {
    return { staticData };
  }

  return {
    ...(tolgeeSSR || {}),
    staticData,
  };
}

/**
 * Lets the locale the client already knows about win over the one baked into a
 * page's SSR props.
 *
 * Next memoizes `getServerSideProps` results per data URL for the life of the
 * SPA session (`router.sdc`, `persistCache: true` in production). Navigating
 * back to a page visited before a language change therefore replays its old
 * props — `getServerSideProps` never re-runs, so the freshly written `i18n`
 * cookie is never read. Tolgee re-applies `ssr.language` on every navigation,
 * which flipped the UI back to the previous language until a full page load.
 *
 * `clientLocale` is the locale `useChangeLocale` stored (see
 * `storePreferredLocale`), already resolved through `getAvailableLocaleFromCode`.
 * Tolgee holds that locale's records from the moment of the switch, so
 * re-asserting it needs no fetch.
 *
 * `enabled` must be false until the app has hydrated — the server has no
 * localStorage, so diverging on the first render would be a hydration mismatch —
 * and on `/lang/[locale]` pages, where the URL is deliberately the source of
 * truth.
 *
 * Note this can reintroduce a `language` that `buildTolgeeSSRProp` dropped for
 * want of matching `staticData`. That rule guards the *initial server render*
 * against a hydration mismatch; `enabled` confines this override to client-side
 * navigations, where there is no server render to mismatch against.
 */
export function applyClientLocaleOverride(
  ssr: TolgeeSSRInput,
  { clientLocale, enabled }: { clientLocale?: string; enabled: boolean },
): TolgeeSSRInput {
  if (!enabled || !clientLocale || clientLocale === ssr.language) {
    return ssr;
  }
  return { ...ssr, language: clientLocale };
}
